// A row that a save in a cell took out of the view: it stays, tinted, for half a second from when it starts to leave,
// so the agent sees the change land, then fades (Shaun, 2026-10-23). Timers rather than transitionend, which a hidden
// tab never fires. The fades must match .inline-row-leaving in main.css.
//
// The tint and the fade belong to the ticket, not to a row element: the list draws each row's class from its ticket
// (leaveClass), because the grid keeps row elements by position - rows below move up into them, and it redraws a
// while after its items change, so an element found by position could be another ticket's.

export const ROW_LEAVE_MS = { hold: 500, fade: 300 };
const REDUCED_MOTION_FADE_MS = 200;

export type LeavePhase = 'held' | 'leaving';
export type LeavingRows<K> = ReadonlyMap<K, LeavePhase>;

// A reload the list draws after its save's request has answered: it is drawn from an effect, and one that arrives while
// other rows are leaving waits for them (the whole hold and fade).
export const FADE_AFTER_RELOAD_MS = ROW_LEAVE_MS.hold + ROW_LEAVE_MS.fade + 500;

/**
 * Whether rows a redraw takes out of the view are fading because a save in a cell moved them: from when the save asks
 * for its reload until a moment after the reload has answered. A window rather than a flag the next redraw uses up,
 * which a redraw that was not the save's reload - its SLAs arriving, a reload held back by other rows leaving -
 * could use up instead, or a save whose reload changed nothing could leave set for the next refresh.
 */
export const saveReloadWindow = (now: () => number = Date.now) => {
  let reloading = 0;
  let until = 0;
  return {
    track: async (reload: () => unknown) => {
      reloading += 1;
      try {
        await reload();
      } finally {
        reloading -= 1;
        until = now() + FADE_AFTER_RELOAD_MS;
      }
    },
    isOpen: () => reloading > 0 || now() < until,
  };
};

/** The keys of the rows shown now that the next rows no longer have. */
export const keysLeaving = <T, K>(shown: readonly T[], next: readonly T[], key: (row: T) => K): K[] => {
  const staying = new Set(next.map(key));
  return shown.map(key).filter(k => !staying.has(k));
};

/**
 * The rows still marked as leaving, less any that `rows` shows - a ticket that comes back into the view is drawn as
 * normal. One that left stays marked: the grid can draw it a moment longer, and it must not flash back first.
 */
export const withoutShown = <T, K>(leaving: LeavingRows<K>, rows: readonly T[], key: (row: T) => K): LeavingRows<K> => {
  const shown = new Set(rows.map(key));
  return new Map([...leaving].filter(([k]) => !shown.has(k)));
};

/** The class a row's ticket gives it while it leaves. */
export const leaveClass = (phase: LeavePhase | undefined) => {
  if (phase === 'leaving') return 'inline-row-held inline-row-leaving';
  if (phase === 'held') return 'inline-row-held';
  return '';
};

/** The leaving rows with these keys added at this phase. */
const markedLeaving = <K>(leaving: LeavingRows<K>, keys: readonly K[], phase: LeavePhase): LeavingRows<K> =>
  new Map([...leaving, ...keys.map(k => [k, phase] as const)]);

const prefersReducedMotion = () =>
  typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Runs a leave: show('held'), show('leaving') after the hold, done() once faded. Returns a function that stops it. */
export const leaveRows = (show: (phase: LeavePhase) => void, done: () => void): (() => void) => {
  const fade = prefersReducedMotion() ? REDUCED_MOTION_FADE_MS : ROW_LEAVE_MS.fade;
  show('held');
  const timers = [
    window.setTimeout(() => show('leaving'), ROW_LEAVE_MS.hold),
    window.setTimeout(done, ROW_LEAVE_MS.hold + fade),
  ];
  return () => timers.forEach(timer => window.clearTimeout(timer));
};

/** What a grid holds for its rows: the rows it draws and the ones on their way out. */
export interface RowLeaveState<T, K> {
  items: T[];
  leaving: LeavingRows<K>;
}

/**
 * The whole leave, for a grid whose rows a cell save can take out of the view - the ticket list (a class) and My Work
 * (a function) both use it, so it holds nothing React does. `show` is given the rows as they now are: rows that left
 * because of a save are held, faded, then dropped, and the newest rows are drawn after them; any other change is drawn
 * at once. `track` wraps a save's reload so its leavers fade. `view.shown` is the rows the grid draws now, and
 * `view.update` writes them back.
 */
export const rowLeaver = <T, K>(
  key: (row: T) => K | undefined,
  view: {
    shown: () => readonly T[];
    update: (next: (prev: RowLeaveState<T, K>) => RowLeaveState<T, K>) => void;
  },
) => {
  const saveReload = saveReloadWindow();
  let stop: (() => void) | undefined;
  let latest: T[] = [];
  // Rows that have finished leaving: view.shown can still hold them, a render behind the update that drops them.
  let removed = new Set<K>();
  const without = (rows: readonly T[]) => rows.filter(row => !removed.has(key(row)));

  const show = (rows: readonly T[]) => {
    latest = [...rows];
    if (stop) return;
    const gone = saveReload.isOpen()
      ? keysLeaving(without(view.shown()), latest, key).filter((k): k is K => k !== undefined)
      : [];
    if (!gone.length) {
      removed = new Set();
      const next = latest;
      view.update(prev => ({ items: next, leaving: withoutShown(prev.leaving, next, key) }));
      return;
    }
    // A new items array, as Fluent draws its rows again only when that changes.
    const mark = (phase: LeavePhase) => view.update(prev => ({
      items: without(prev.items),
      leaving: markedLeaving(prev.leaving, gone, phase),
    }));
    stop = leaveRows(mark, () => {
      stop = undefined;
      // Then the rows as they now are - a reload that came in meanwhile has its own leavers faded too.
      removed = new Set([...removed, ...gone]);
      show(latest);
    });
  };

  return {
    show,
    track: saveReload.track,
    /** Stops a leave part-way - the grid is going. */
    stop: () => { stop?.(); stop = undefined; },
  };
};
